import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, mergeMap, of, switchMap } from 'rxjs';
import { Observable } from 'rxjs';
import { CamerasService } from '../../cameras/cameras.service';
import { PtzModels } from '../../cameras/ptz.model';
import { UtilsService } from '../../edge/utils.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { PtzControlActions } from '@states/ptz-control/ptz-control.actions';
import { TranslateService } from '@ngx-translate/core';

function commandResultActions(
  result: PtzModels.PtzCommandResult,
): Action[] {
  const failureMessage = PtzModels.getCommandFailureMessage(result?.failure);
  return failureMessage
    ? [SharedActions.showMessage({ error: failureMessage })]
    : [];
}

function commandErrorActions(
  error: HttpErrorResponse | Error,
  utilsService: UtilsService,
  translateService: TranslateService,
  fallback = 'PTZ.ERRORS.COMMAND_FAILED',
): Action[] {
  return [
    SharedActions.showMessage({
      error: utilsService.errMessage(error) || translateService.instant(fallback),
    }),
  ];
}

export const sendPtzCommand$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(
        PtzControlActions.move,
        PtzControlActions.stop,
        PtzControlActions.goHome,
        PtzControlActions.setHome,
        PtzControlActions.goToPreset,
      ),
      mergeMap((action) => {
        let command$: Observable<PtzModels.PtzCommandResult>;
        switch (action.type) {
          case PtzControlActions.move.type:
            command$ = camerasService.cameraPtz(action.request);
            break;
          case PtzControlActions.stop.type:
            command$ = camerasService.cameraPtzStop(action.request);
            break;
          case PtzControlActions.goHome.type:
            command$ = camerasService.cameraPtzHome(action.request);
            break;
          case PtzControlActions.setHome.type:
            command$ = camerasService.cameraPtzSaveHome(action.request);
            break;
          case PtzControlActions.goToPreset.type:
            command$ = camerasService.cameraPtzGotoPreset(action.request);
            break;
        }
        return command$.pipe(
          mergeMap((result) =>
            commandResultActions(result),
          ),
          catchError((error) =>
            of(...commandErrorActions(error, utilsService, translateService)),
          ),
        );
      }),
    ),
  { functional: true },
);

export const runPtzTest$ = createEffect(
  (actions$ = inject(Actions), camerasService = inject(CamerasService), utilsService = inject(UtilsService), translateService = inject(TranslateService)) =>
    actions$.pipe(
      ofType(PtzControlActions.runTest),
      switchMap(({ request }) =>
        camerasService.cameraPtzTest(request).pipe(
          mergeMap((result) => {
            const failureMessage = PtzModels.getCommandFailureMessage(
              result?.failure,
            );
            return failureMessage
              ? [PtzControlActions.runTestFail(), SharedActions.showMessage({ error: failureMessage })]
              : [PtzControlActions.runTestSuccess({ report: result?.test ?? null })];
          }),
          catchError((error) =>
            of(
              PtzControlActions.runTestFail(),
              ...commandErrorActions(error, utilsService, translateService, 'PTZ.ERRORS.TEST_FAILED'),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);
